import { Label as LabelIcon } from "@mui/icons-material";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import CancelButton from "../cancelButton/CancelButton";
import TagCategoryColumns from "./TagCategoryColumns";
import TagSelectField from "./TagSelectField";

export interface SelectedTag {
  readonly category: string;
  readonly value: string;
}

interface TagsDialogProps {
  readonly open: boolean;
  readonly availableTags: Record<string, string[]>;
  readonly tags: SelectedTag[];
  readonly onChange: (tags: SelectedTag[]) => void;
  readonly onClose: () => void;
}

const TagsDialog: FC<TagsDialogProps> = ({ open, tags, availableTags, onClose, onChange }) => {
  const [selectedTags, setSelectedTags] = useState(tags);
  useEffect(() => {
    setSelectedTags(tags);
  }, tags);
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <DialogTitle>Tagi</DialogTitle>
      <DialogContent>
        <TagCategoryColumns
          availableTags={availableTags}
          renderControl={(category, value) => (
            <Checkbox
              checked={!!selectedTags.find((st) => st.value === value && st.category === category)}
              onChange={(_, checked) =>
                setSelectedTags((prev) =>
                  checked
                    ? [...prev, { value, category }]
                    : prev.filter((prevTag) => prevTag.value !== value || prevTag.category !== category),
                )
              }
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <CancelButton
          onClick={() => {
            setSelectedTags(tags);
            onClose();
          }}
        />
        <Button color="primary" onClick={() => onChange(selectedTags)}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TagSelectVariantProps {
  readonly label: string;
  readonly tags: SelectedTag[];
  readonly readonly?: boolean;
  readonly onOpen: () => void;
}

const TagSelectIcon: FC<TagSelectVariantProps> = ({ label, tags, readonly, onOpen }) => (
  <Tooltip title={label}>
    <IconButton aria-label={label} onClick={onOpen} disabled={readonly}>
      <Badge badgeContent={tags.length} color="primary" invisible={tags.length === 0}>
        <LabelIcon />
      </Badge>
    </IconButton>
  </Tooltip>
);

interface Props {
  readonly label?: string;
  readonly tags: SelectedTag[];
  readonly availableTags: Record<string, string[]>;
  readonly readonly?: boolean;
  readonly maxVisibleTags?: number;
  readonly variant?: "field" | "icon";
  readonly onChange: (tags: SelectedTag[]) => void;
}

const TagSelect: FC<Props> = ({
  label = "Tagi",
  tags,
  availableTags,
  readonly,
  maxVisibleTags = 5,
  variant = "field",
  onChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const variantProps: TagSelectVariantProps = {
    label,
    tags,
    readonly,
    onOpen: () => setDialogOpen(true),
  };

  return (
    <>
      {variant === "icon" ? (
        <TagSelectIcon {...variantProps} />
      ) : (
        <TagSelectField {...variantProps} maxVisibleTags={maxVisibleTags} />
      )}
      <TagsDialog
        key={tags.join(";")}
        open={dialogOpen}
        tags={tags}
        availableTags={availableTags}
        onClose={() => setDialogOpen(false)}
        onChange={(nextTags) => {
          onChange(nextTags);
          setDialogOpen(false);
        }}
      />
    </>
  );
};

export default TagSelect;
